// Historial de auditoría con filtros (Tanda 3 — Blindaje de datos). Reúne
// TODA la actividad relevante de la cuenta: cambios sincronizados de
// animales/fincas/lotes/eventos/inventarios, acciones del motor de
// plantillas (muerte, venta, palpación, compra, etc.), papelera y
// restauraciones de backup — todo owner-scoped, así el dueño ve también lo
// que hicieron sus encargados (antes solo se veía la actividad propia).
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import * as api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, ScrollText, User as UserIcon, MapPin, Layers, Calendar, ClipboardList, Database, Trash2 } from "lucide-react";
import { formatDateTime } from "@/lib/utils";
import { useOnline } from "@/lib/useOnline";
import { OfflineNotice } from "@/components/OfflineNotice";

const ENTITY_OPTIONS = [
  { value: "_all", label: "Todos" },
  { value: "animal", label: "Animal" },
  { value: "accion", label: "Acción" },
  { value: "farm", label: "Finca" },
  { value: "lot", label: "Lote" },
  { value: "event", label: "Evento" },
  { value: "inventory", label: "Inventario" },
  { value: "backup", label: "Backup" },
];

const ENTITY_ICON = { animal: Layers, accion: ClipboardList, farm: MapPin, lot: MapPin, event: Calendar, inventory: ClipboardList, backup: Database };
const OP_LABEL = {
  create: "Alta", update: "Modificación", delete: "Eliminó", restore: "Restauró",
  restore_bulk: "Restauró (masivo)", bulk_delete: "Eliminó (masivo)", conflict_resolved: "Conflicto resuelto",
};

const LIMIT = 30;

export default function AuditLog() {
  const navigate = useNavigate();
  const online = useOnline();
  const [logs, setLogs] = useState([]);
  const [handlers, setHandlers] = useState([]);
  const [entity, setEntity] = useState("_all");
  const [actor, setActor] = useState("_all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  useEffect(() => { api.listHandlers().then(setHandlers).catch(() => {}); }, []);

  const load = useCallback(async (nextSkip = 0) => {
    if (!online) return;
    setLoading(true);
    try {
      const params = { limit: LIMIT, skip: nextSkip };
      if (entity !== "_all") params.entity = entity;
      if (actor !== "_all") params.actor = actor;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = `${dateTo}T23:59:59`;
      const res = await api.listSyncLogs(params);
      setLogs((prev) => (nextSkip === 0 ? res : [...prev, ...res]));
      setHasMore(res.length === LIMIT);
      setSkip(nextSkip);
    } finally { setLoading(false); }
  }, [entity, actor, dateFrom, dateTo, online]);

  useEffect(() => { load(0); }, [load]);

  return (
    <div className="max-w-2xl mx-auto space-y-4">
      <div className="flex items-center gap-2">
        <Button variant="ghost" onClick={() => navigate("/ajustes")} className="h-9 px-2"><ArrowLeft size={18} /></Button>
        <h1 className="font-heading text-2xl font-semibold flex-1 flex items-center gap-2"><ScrollText size={20} /> Historial de auditoría</h1>
      </div>
      <p className="text-sm text-muted-foreground -mt-2">Quién hizo cada cambio, con filtros. Incluye tu actividad y la de tus encargados.</p>

      {!online && <OfflineNotice label="El historial de auditoría requiere conexión." />}

      {online && (
        <Card data-testid="audit-filters-card">
          <CardContent className="p-4 grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-muted-foreground">Tipo</label>
              <Select value={entity} onValueChange={setEntity}>
                <SelectTrigger className="h-10 mt-1" data-testid="audit-filter-entity"><SelectValue /></SelectTrigger>
                <SelectContent>{ENTITY_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Quién</label>
              <Select value={actor} onValueChange={setActor}>
                <SelectTrigger className="h-10 mt-1" data-testid="audit-filter-actor"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_all">Todos</SelectItem>
                  {handlers.map((h) => <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Desde</label>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-10 mt-1" data-testid="audit-filter-date-from" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Hasta</label>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-10 mt-1" data-testid="audit-filter-date-to" />
            </div>
          </CardContent>
        </Card>
      )}

      {online && (
        <Card>
          <CardContent className="p-0 divide-y divide-border">
            {!loading && logs.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-10" data-testid="audit-empty">Sin actividad para estos filtros.</p>
            )}
            {logs.map((l) => {
              const Icon = ENTITY_ICON[l.entity] || Trash2;
              const label = l.summary || OP_LABEL[l.operation] || l.operation;
              return (
                <div key={l.id} className="p-3.5 flex items-start gap-3" data-testid={`audit-row-${l.id}`}>
                  <div className="w-8 h-8 rounded-md bg-muted grid place-items-center shrink-0 mt-0.5"><Icon size={14} /></div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium">{label}</div>
                    <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-2 flex-wrap">
                      <span className="inline-flex items-center gap-1"><UserIcon size={11} />{l.actor_name || "—"}</span>
                      {l.actor_role === "handler" && <Badge variant="outline" className="text-[10px] py-0">Encargado</Badge>}
                      <span>·</span>
                      <span>{formatDateTime(l.server_received_at)}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {online && hasMore && logs.length > 0 && (
        <Button variant="outline" className="w-full" onClick={() => load(skip + LIMIT)} disabled={loading} data-testid="audit-load-more">
          {loading ? "Cargando…" : "Cargar más"}
        </Button>
      )}
    </div>
  );
}
